import {
    ActorKind,
    ActorType,
    AuditChannel,
    AuditLogChangeType,
} from "@common/types/auditLog";
import { APIProcessName } from "@common/constants/process-names";
import {
    KICK_ACTOR_LABEL,
    KICK_AGENT_ACTOR_LABEL,
    KICK_SUPPORT_LABEL,
    KICK_RULES_LABEL,
    PLATFORM_API_ACTOR_LABEL,
} from "@common/constants/actor";
import {
    AUDIT_CHANNEL_LABELS,
    HistoryDetailsChannel,
    NO_HISTORY_CHANNEL,
} from "@common/constants/agent-channel";
import { FIELD_UPDATE_SOURCE } from "@common/constants";
import {
    INTEGRATION_OWNED_FIELDS,
    INTEGRATION_SYSTEM_SOURCES,
    RULE_FIELD_UPDATE_SOURCES,
    RULE_SOURCE_TYPES,
} from "@common/helpers/historyActorPredicates";
import { integrationActors } from "../components/integrationActors";

export const DEFAULT_ICON_COLOR = "#6366f1";

const RULE_SOURCE_TYPES_SET: ReadonlySet<string> = new Set(RULE_SOURCE_TYPES);

const USER_TRANSACTION_API_SOURCE_TYPES: ReadonlySet<string> = new Set([
    APIProcessName.TRANSACTION_BULK_UPDATE,
    APIProcessName.TRANSACTION_UPDATE,
    APIProcessName.TRANSACTION_MATCH_TRANSFER,
    APIProcessName.TRANSACTION_UNMATCH_TRANSFER,
]);

const RULE_FIELD_UPDATE_SOURCES_SET: ReadonlySet<FIELD_UPDATE_SOURCE> = new Set(
    RULE_FIELD_UPDATE_SOURCES,
);

const INTEGRATION_SYSTEM_SOURCES_SET: ReadonlySet<FIELD_UPDATE_SOURCE> =
    new Set(INTEGRATION_SYSTEM_SOURCES);

const INTEGRATION_OWNED_FIELDS_SET: ReadonlySet<string> = new Set<string>(
    INTEGRATION_OWNED_FIELDS,
);

interface ActorKindParams {
    actorType: string;
    sourceType: string;
    fields?: string[];
    changeTypes?: AuditLogChangeType[];
    system?: string | null;
    isAgentActor?: boolean;
}

function isIntegrationOwnedRow(
    fields: string[] | undefined,
    changeTypes: AuditLogChangeType[] | undefined,
): boolean {
    if (fields === undefined) {
        return true;
    }
    if (
        changeTypes?.length === 1 &&
        changeTypes[0] === AuditLogChangeType.CREATE
    ) {
        return true;
    }
    return (
        fields.length > 0 &&
        fields.every((field) => INTEGRATION_OWNED_FIELDS_SET.has(field))
    );
}

const FIELD_UPDATE_SOURCE_VALUES: ReadonlySet<string> = new Set(
    Object.values(FIELD_UPDATE_SOURCE),
);

function isFieldUpdateSource(value: string): value is FIELD_UPDATE_SOURCE {
    return FIELD_UPDATE_SOURCE_VALUES.has(value);
}

function isRuleSystemSource(system: string | null | undefined): boolean {
    if (!system) {
        return false;
    }
    if (!isFieldUpdateSource(system)) {
        return false;
    }
    return RULE_FIELD_UPDATE_SOURCES_SET.has(system);
}

function isIntegrationSystemSource(system: string | null | undefined): boolean {
    if (!system) {
        return false;
    }
    if (!isFieldUpdateSource(system)) {
        return false;
    }
    return INTEGRATION_SYSTEM_SOURCES_SET.has(system);
}

function isIntegrationSystemUpdate(
    system: string | null | undefined,
    changeTypes: AuditLogChangeType[] | undefined,
): boolean {
    if (!isIntegrationSystemSource(system)) {
        return false;
    }
    // Mirror the backend integration predicate, which credits integration-system
    // rows only on UPDATE (there is no DELETE integration predicate). CREATE rows
    // are already handled by isIntegrationOwnedRow before this is reached.
    return changeTypes?.includes(AuditLogChangeType.UPDATE) ?? false;
}

export function resolveActorKind(params: ActorKindParams): ActorKind {
    const { actorType, sourceType, fields, changeTypes, system, isAgentActor } =
        params;

    if (isAgentActor) {
        return ActorKind.AGENT;
    }

    if (RULE_SOURCE_TYPES_SET.has(sourceType)) {
        return ActorKind.RULE;
    }

    if (integrationActors[sourceType]) {
        if (
            isIntegrationOwnedRow(fields, changeTypes) ||
            isIntegrationSystemUpdate(system, changeTypes)
        ) {
            return ActorKind.INTEGRATION;
        }
    }

    if (isRuleSystemSource(system)) {
        return ActorKind.RULE;
    }

    if (
        actorType === ActorType.USER &&
        USER_TRANSACTION_API_SOURCE_TYPES.has(sourceType)
    ) {
        return ActorKind.USER;
    }

    if (integrationActors[sourceType]) {
        return ActorKind.KICK;
    }

    if (actorType === ActorType.SYSTEM || actorType === ActorType.SUPPORT) {
        return ActorKind.KICK;
    }

    if (actorType === ActorType.ORGANIZATION) {
        return ActorKind.ORGANIZATION;
    }

    return ActorKind.USER;
}

interface ActorDisplayNameParams extends ActorKindParams {
    actorName: string | null;
    currentUserId?: number;
    actorId: number | null;
    memberName?: string | null;
}

export function resolveActorDisplayName(
    params: ActorDisplayNameParams,
): string {
    const { actorName, sourceType, currentUserId, actorId, memberName } =
        params;

    const kind = resolveActorKind(params);

    if (kind === ActorKind.INTEGRATION) {
        return integrationActors[sourceType]?.label ?? sourceType;
    }

    if (kind === ActorKind.RULE) {
        return KICK_RULES_LABEL;
    }

    if (kind === ActorKind.AGENT) {
        return KICK_AGENT_ACTOR_LABEL;
    }

    if (currentUserId && actorId !== null && actorId === currentUserId) {
        return "You";
    }

    if (kind === ActorKind.KICK) {
        return params.actorType === ActorType.SUPPORT
            ? KICK_SUPPORT_LABEL
            : KICK_ACTOR_LABEL;
    }

    if (kind === ActorKind.ORGANIZATION) {
        return PLATFORM_API_ACTOR_LABEL;
    }

    return memberName ?? actorName ?? "Unknown";
}

export function resolveActorChannelLabel(
    params: ActorKindParams & { channel?: AuditChannel | null },
): string | undefined {
    const { channel } = params;
    if (
        !channel ||
        channel === AuditChannel.WEB ||
        resolveActorKind(params) !== ActorKind.USER
    ) {
        return undefined;
    }
    return AUDIT_CHANNEL_LABELS[channel];
}

// Maps a row's channel to the details-drawer discriminator. A null channel is
// a legacy row without a channel (direct PAT before channels were stored) and
// maps to the explicit "none" value: axios drops null query params, which
// would widen the drawer.
export function toDetailsChannel(
    channel: AuditChannel | null | undefined,
): HistoryDetailsChannel {
    return channel ?? NO_HISTORY_CHANNEL;
}
